'use client';

/**
 * "Run without asking me" — one capability's standing approval.
 *
 * Renders ONLY for a tool the server said is grantable. That set is derived
 * server-side and excludes every outward send, publish, delete, merge, money
 * and access-control action, so the absence of a toggle is itself the answer
 * to "can I stop being asked about this one?" — no disabled control to
 * misread, no explanation of a rule the reader cannot change.
 */

import React from 'react';
import { Check, Loader2 } from 'lucide-react';

export interface AutoApproveToggleProps {
  toolName: string;
  isOn: boolean;
  isSaving: boolean;
  onChange: (next: boolean) => void;
}

export function AutoApproveToggle({
  toolName,
  isOn,
  isSaving,
  onChange,
}: AutoApproveToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={isOn}
      aria-label={`Run ${toolName} without asking me`}
      disabled={isSaving}
      onClick={() => onChange(!isOn)}
      title={
        isOn
          ? 'Runs without asking. Click to be asked again.'
          : "You'll be asked to confirm. Click to stop asking."
      }
      className={[
        'inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors',
        'disabled:opacity-60',
        isOn
          ? 'bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/25 dark:text-emerald-400'
          : 'bg-amber-500/15 text-amber-700 hover:bg-amber-500/25 dark:text-amber-400',
      ].join(' ')}
    >
      {isSaving ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : isOn ? (
        <Check className="h-3 w-3" />
      ) : null}
      {/* The off state keeps the exact wording the badge has always had, so a
          reader scanning for "Requires approval" still finds it and the
          characterization tests still describe something real. */}
      {isOn ? 'Runs automatically' : 'Requires approval'}
    </button>
  );
}
